import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { toast } from '@/lib/toast';
import { usePageTitle } from '@/hooks/usePageTitle';

const OAUTH_LABELS: Record<string, { label: string; icon: string }> = {
  google: { label: 'Google', icon: 'G' },
  discord: { label: 'Discord', icon: 'D' },
};

export function LoginView() {
  usePageTitle('Log In');
  const { user, login, register, loading, authConfig } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState<'login' | 'register'>(params.get('tab') === 'register' ? 'register' : 'login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [inviteToken, setInviteToken] = useState(params.get('invite') ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const returnTo = params.get('returnTo') ?? '/';

  useEffect(() => {
    if (!loading && user) {
      navigate(returnTo, { replace: true });
    }
  }, [user, loading, navigate, returnTo]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (tab === 'login') {
        await login(email, password);
        toast.success('Welcome back!');
      } else {
        await register(email, password, displayName, inviteToken || undefined);
        toast.success('Account created!');
      }
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <span className="text-on-surface-variant">Loading…</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-[400px] bg-surface-container rounded-lg p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
        {/* Logo */}
        <div className="text-center mb-6">
          <img
            src="/static/images/meta-command-logo.png"
            alt="MetaCommand"
            className="h-16 object-contain"
          />
        </div>

        {/* Tabs */}
        <div className="flex bg-surface-container-high rounded-sm mb-6 overflow-hidden">
          {(['login', 'register'] as const).map((t) => (
            <button
              key={t}
              onClick={() => { setTab(t); setError(''); }}
              className={`flex-1 py-2.5 border-none cursor-pointer font-semibold text-md capitalize ${
                tab === t
                  ? 'bg-primary text-on-primary'
                  : 'bg-transparent text-on-surface'
              }`}
            >
              {t === 'login' ? 'Log In' : 'Register'}
            </button>
          ))}
        </div>

        {/* OAuth SSO buttons */}
        {authConfig?.oauth_providers && authConfig.oauth_providers.length > 0 && (
          <div className="flex flex-col gap-2 mb-4">
            {authConfig.oauth_providers.map((provider) => {
              const meta = OAUTH_LABELS[provider] ?? { label: provider, icon: provider[0].toUpperCase() };
              return (
                <a
                  key={provider}
                  href={`/api/v1/auth/login/${provider}`}
                  className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-sm border border-outline-variant bg-surface-container-high text-on-surface no-underline font-medium text-md cursor-pointer"
                >
                  <span className="text-lg font-bold w-6 text-center">{meta.icon}</span>
                  Continue with {meta.label}
                </a>
              );
            })}
            <div className="flex items-center gap-3 my-1">
              <div className="flex-1 h-px bg-outline-variant" />
              <span className="text-sm text-on-surface-variant">or</span>
              <div className="flex-1 h-px bg-outline-variant" />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          {tab === 'register' && (
            <input
              type="text"
              aria-label="Display name"
              placeholder="Display name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              className="input-field"
            />
          )}
          <input
            type="email"
            aria-label="Email address"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="input-field"
          />
          <input
            type="password"
            aria-label="Password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            className="input-field"
          />
          {tab === 'register' && (
            <input
              type="text"
              aria-label="Invite token"
              placeholder="Invite token (if required)"
              value={inviteToken}
              onChange={(e) => setInviteToken(e.target.value)}
              className="input-field"
            />
          )}

          {error && (
            <div className="py-2.5 px-3.5 bg-status-error-bg rounded-sm text-error-text text-base">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full py-3 rounded-sm text-[15px]"
          >
            {submitting ? '…' : tab === 'login' ? 'Log In' : 'Create Account'}
          </button>
        </form>

        <div className="text-center mt-4 text-base text-on-surface-variant">
          {tab === 'login' ? (
            <>Don&apos;t have an account?{' '}
              <button onClick={() => setTab('register')} className="bg-transparent border-none text-primary cursor-pointer text-base">
                Register
              </button>
            </>
          ) : (
            <>Already have an account?{' '}
              <button onClick={() => setTab('login')} className="bg-transparent border-none text-primary cursor-pointer text-base">
                Log In
              </button>
            </>
          )}
        </div>

        {/* Back to public decks */}
        <div className="text-center mt-3">
          <Link to="/decks" className="text-sm text-on-surface-variant">
            Browse decks without logging in →
          </Link>
        </div>
      </div>
    </div>
  );
}
